import { ODataOptions } from 'src/util/ODataBuilder';
import BaseApi from './BaseApi';
import ProjectModel from 'src/models/Project';

import { IAppContextState } from 'src/reducers/AppContext';
import { UrlFactory } from './urlHelper';

export interface IProject {
    getAll(clientNumber: number, options: ODataOptions, jwt: string): Promise<object>;
    get(id: number, projectId: number, jwt: string): Promise<object>;
    create(project: ProjectModel, jwt: string): Promise<object>;
    refreshIntegrated(clientNumber: number, jwt: string): Promise<object>;
    update(project: ProjectModel, jwt: string): Promise<object>;
    addProjectFavorite(clientNumber: number, projectNumber: number, jwt: string): Promise<object>;
    removeProjectFavorite(clientNumber: number, projectNumber: number, jwt: string): Promise<object>;
    getStaffing(clientId: number, projectId: number, jwt: string): Promise<object>;
    getAdministration(clientNumber: number, projectNumber: number, jwt: string): Promise<object>;
    getHorizonIntervals(projectId: number, jwt: string): Promise<object>;
    getTractTypes(clientNumber: number, jwt: string): Promise<object>;
    updateCommittedCategoriesToggle(project: ProjectModel, jwt: string): Promise<object>;
}

export default class Project extends BaseApi implements IProject {
    public getAll = (clientNumber: number, options: ODataOptions, jwt: string) => {
        return this.getRequest(`${this.baseUrl}v1.0/clients/${clientNumber}/projects/${options.toString()}`, jwt);
    };

    public get = (clientId: number, projectId: number, jwt: string) => {
        return this.getRequest(`${this.baseUrl}v1.0/clients/${clientId}/projects/${projectId}`, jwt);
    };

    public create = (project: ProjectModel, jwt: string) => {
        return this.postRequest(`${this.baseUrl}v1.0/clients/${project.client.id}/projects`, project, jwt);
    };

    public refreshIntegrated = (clientNumber: number, jwt: string) => {
        return this.postRequest(`${this.baseUrl}v1.0/clients/${clientNumber}/projects/refresh-integrated-projects`, {}, jwt);
    };

    public update = (project: ProjectModel, jwt: string) => {
        return this.putRequest(`${this.baseUrl}v1.0/clients/${project.client.id}/projects/${project.projectNumber}`, project, jwt);
    };

    public addProjectFavorite = (clientNumber: number, projectNumber: number, jwt: string) => {
        return this.putRequest(`${this.baseUrl}v1.0/clients/${clientNumber}/projects/${projectNumber}/favorite`, undefined, jwt);
    };

    public removeProjectFavorite = (clientNumber: number, projectNumber: number, jwt: string) => {
        return this.deleteRequest(`${this.baseUrl}v1.0/clients/${clientNumber}/projects/${projectNumber}/favorite`, undefined, jwt);
    };

    public getStaffing = (clientId: number, projectId: number, jwt: string) => {
        return this.getRequest(`${this.baseUrl}v1.0/clients/${clientId}/projects/${projectId}/projectstaff/staffing`, jwt);
    };

    public getAdministration = (clientNumber: number, projectNumber: number, jwt: string) => {
        return this.getRequest(`${this.baseUrl}v1.0/clients/${clientNumber}/projects/${projectNumber}/projectstaff/administration`, jwt);
    };

    public getHorizonIntervals = (projectId: number, jwt: string) => {
        return this.getRequest(`${this.baseUrl}v1.0/projects/${projectId}/horizon-intervals`, jwt);
    };

    public getTractTypes = (clientNumber: number, jwt: string) => {
        return this.getRequest(`${this.baseUrl}v1.0/clients/${clientNumber}/projects/tract-types`, jwt);
    };

    public updateCommittedCategoriesToggle = (project: ProjectModel, jwt: string) => {
        return this.putRequest(`${this.baseUrl}v1.0/clients/${project.client.id}/projects/${project.projectNumber}/update-committed-category-toggle`, project, jwt);
    }

    // Refactor Change
}

export const ProjectUrl = {
    base: (appContext: IAppContextState) => `${UrlFactory.ForProject(appContext)}`,
    getProject: (appContext: IAppContextState, projectNumber: number) => `${UrlFactory.ForClient(appContext)}/projects/${projectNumber}`,
    getProjectCounties: (appContext: IAppContextState) => `${UrlFactory.ForProject(appContext)}/counties`,
    getAreas: (appContext: IAppContextState) => `${UrlFactory.ForProject(appContext)}/areas`,
    getTownshipCounties: (appContext: IAppContextState, townshipId: number) =>
        `${UrlFactory.ForProject(appContext)}/townships/${townshipId}/counties`,
    getEasternTownshipCounties: (appContext: IAppContextState, townshipId: number) =>
        `${UrlFactory.ForProject(appContext)}/eastern-townships/${townshipId}/counties`,
    getProjectTags: (appContext: IAppContextState) => `${UrlFactory.ForProject(appContext)}/tags`,
    addProjectTags: (appContext: IAppContextState) => `${UrlFactory.ForProject(appContext)}/tags`,
    getTownships: (appContext: IAppContextState) => `${UrlFactory.ForProject(appContext)}/townships`,
    getTownship: (appContext: IAppContextState, townshipId: number) => `${UrlFactory.ForProject(appContext)}/townships/${townshipId}`,
    getSectionsForTownship: (appContext: IAppContextState, townshipId: number) =>
        `${UrlFactory.ForProject(appContext)}/townships/${townshipId}/sections`,
    getEasternTownships: (appContext: IAppContextState) => `${UrlFactory.ForProject(appContext)}/eastern-townships`,
    getEasternTownship: (appContext: IAppContextState, townshipId: number) => `${UrlFactory.ForProject(appContext)}/eastern-townships/${townshipId}`,
    getEasternMapsByTownship: (appContext: IAppContextState, townshipId: number) =>
        `${UrlFactory.ForProject(appContext)}/eastern-townships/${townshipId}/maps`,
    getEasternMap: (appContext: IAppContextState, mapNumber: number) => `${UrlFactory.ForProject(appContext)}/maps/${mapNumber}`,
    getEasternMapAcreage: (appContext: IAppContextState, mapNumber: number) => `${UrlFactory.ForProject(appContext)}/maps/${mapNumber}/acreage`,
    getSectionsByTownship: (appContext: IAppContextState, townshipId: number) =>
        `${UrlFactory.ForProject(appContext)}/townships/${townshipId}/sections`,
    getSections: (appContext: IAppContextState) => `${UrlFactory.ForProject(appContext)}/sections`,
    getSection: (appContext: IAppContextState, sectionNumber: number) => `${UrlFactory.ForProject(appContext)}/sections/${sectionNumber}`,
    getSectionAcreage: (appContext: IAppContextState, sectionNumber: number) =>
        `${UrlFactory.ForProject(appContext)}/sections/${sectionNumber}/acreage`,
    getProjectMeridians: (appContext: IAppContextState) => `${UrlFactory.ForProject(appContext)}/meridians`,
    getSectionCounties: (appContext: IAppContextState, sectionNumber: number) =>
        `${UrlFactory.ForProject(appContext)}/townships/1/sections/${sectionNumber}/tracts/1/counties`,
    getFieldCounties: (appContext: IAppContextState, fieldNumber: number) =>
        `${UrlFactory.ForProject(appContext)}/fields/${fieldNumber}/counties`,
    getMapCounties: (appContext: IAppContextState, mapNumber: number) => `${UrlFactory.ForProject(appContext)}/maps/${mapNumber}/counties`,
    getAbstract: (appContext: IAppContextState, countyId: number, abstractNumber: string) =>
        `${UrlFactory.ForProject(appContext)}/abstracts/${countyId}/${abstractNumber}`,
    getAbstractById: (appContext: IAppContextState, abstractId: number) => `${UrlFactory.ForProject(appContext)}/abstracts/${abstractId}`,
    deleteTownship: (appContext: IAppContextState, townshipId: number) => `${UrlFactory.ForProject(appContext)}/townships/${townshipId}/delete-township`,
};



// WEBPACK FOOTER //
// ./src/api/Project.ts